import {
  SearchAutocomplete,
  SearchMenu,
  Box,
  Text,
} from '@csscompany/csds-components';
import { rosetta, useFacilitiesInfo } from '@csscompany/op-facility-finder';
import React, { useCallback, useMemo } from 'react';

import { getCountryCodeMap } from '../../constants/countryCodes';
import {
  LOG_PRODUCT_NAME,
  LOCATIONS_COMPONENT_NAME,
} from '../../constants/facilityConstants';
import { SearchedLocation, useSearch } from '../../hooks/useSearchLocation';
import { useThrottledState } from '../../hooks/useThrottledState';
import { useIsMobile } from '../../utils/breakpoints';
import { useMixpanel } from '../../utils/mixpanel';

// Default countries for search box in cloudkitchens.com - used as fallback
const DEFAULT_CK_COUNTRIES = ['us', 'ca'];

type LocationsSearchBoxProps = {
  siteLang: string;
};

export const LocationsSearchBox = ({
  siteLang,
}: LocationsSearchBoxProps): JSX.Element | null => {
  const { facilitiesData, setViewInfo } = useFacilitiesInfo();

  const mixpanelClient = useMixpanel();
  const isMobile = useIsMobile();

  const countries = useMemo(() => {
    if (facilitiesData.length === 0) {
      return DEFAULT_CK_COUNTRIES;
    }
    const countriesSet = new Set<string>();
    const countryCodeMap = getCountryCodeMap();
    facilitiesData.forEach((facility) => {
      if (!countryCodeMap.has(facility.country?.toLowerCase() ?? '')) {
        console.warn(`Country code not found for ${facility.country}`);
      }
      countriesSet.add(
        countryCodeMap.get(facility.country.toLowerCase()) ?? ''
      );
    });

    return Array.from(countriesSet);
  }, [facilitiesData]);

  const [searchAddress, setSearchAddress] = useThrottledState('', 600);
  const { isLoading: isSearching, data: locations } = useSearch(
    searchAddress,
    countries,
    siteLang
  );

  const onSelectionChange = useCallback(
    (location) => {
      setSearchAddress('');
      if (location) {
        const { address, longitude, latitude } = location;
        const level = address.split(',').length;
        // TODO: Figure out Zoom info from the bounding-box in the MapBox response
        setViewInfo(
          { longitude, latitude },
          isMobile
            ? level === 1
              ? 5
              : level === 2
                ? 7
                : 9
            : level === 1
              ? 5.5
              : level === 2
                ? 9
                : 10
        );
        mixpanelClient.track(
          {
            product: LOG_PRODUCT_NAME,
            component: LOCATIONS_COMPONENT_NAME,
            event: 'select search location',
          },
          {
            location,
          }
        );
      }
    },
    [isMobile]
  );

  const onChange = useCallback(async (value) => {
    setSearchAddress(value);
  }, []);

  return (
    <SearchAutocomplete<SearchedLocation>
      dataTest="search-location-combobox"
      onChange={onChange}
      options={locations || []}
      loading={isSearching}
      placeholder={rosetta.location_search_placeholder.getText()}
      onSelect={onSelectionChange}
      optionToString={(location) => location.name}
      renderMenu={(props) => (
        <SearchMenu
          {...props}
          empty={
            isSearching
              ? rosetta.location_search_loading.getText()
              : searchAddress
                ? rosetta.location_search_empty_results.getText({
                    searchAddress,
                  })
                : rosetta.location_search_prompt.getText()
          }
          toMenuItem={(item: SearchedLocation) => ({
            value: item.id,
            label: (
              <Box
                key={item.id}
                dataTest={`${item.id}-box`}
                display="flex"
                flexDirection="column"
                gap="$1">
                <Text variant="$labelSmall" color="$staticTextStandard">
                  {item.name}
                </Text>
                <Text variant="$paragraphSmall" color="$staticTextWeak">
                  {item.address}
                </Text>
              </Box>
            ),
          })}
        />
      )}
    />
  );
};
